'use client';

import { InputHTMLAttributes, forwardRef, useState, useCallback, createContext, useContext, KeyboardEvent } from 'react';
import styles from './RadioButton.module.css';

/* ===== RADIO GROUP CONTEXT ===== */
interface RadioGroupContextType {
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextType | null>(null);

/* ===== RADIO GROUP ===== */
interface RadioGroupProps {
  name: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  direction?: 'vertical' | 'horizontal';
  children: React.ReactNode;
  className?: string;
}

export function RadioGroup({
  name,
  value,
  defaultValue = '',
  onChange,
  disabled = false,
  direction = 'vertical',
  children,
  className = '',
}: RadioGroupProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = value !== undefined ? value : internalValue;

  const handleChange = useCallback((val: string) => {
    if (value === undefined) {
      setInternalValue(val);
    }
    onChange?.(val);
  }, [onChange, value]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    const radios = Array.from(
      e.currentTarget.querySelectorAll<HTMLInputElement>('input[type="radio"]:not(:disabled)')
    );
    const currentIndex = radios.findIndex(r => r.value === currentValue);
    let nextIndex = currentIndex;

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      nextIndex = (currentIndex + 1) % radios.length;
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      nextIndex = (currentIndex - 1 + radios.length) % radios.length;
    }

    if (nextIndex !== currentIndex && radios[nextIndex]) {
      // literal index (D9 class 2): radios[nextIndex] was just truthy-checked above (same
      // expression, no mutation in between) — 0 risk.
      radios[nextIndex]!.focus();
      handleChange(radios[nextIndex]!.value);
    }
  }, [currentValue, handleChange]);

  const groupClasses = [
    styles.group,
    direction === 'horizontal' ? styles.groupHorizontal : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <RadioGroupContext.Provider value={{ name, value: currentValue, onChange: handleChange, disabled }}>
      <div className={groupClasses} role="radiogroup" onKeyDown={handleKeyDown}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

/* ===== RADIO BUTTON ===== */
interface RadioButtonProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: string;
  value: string;
}

const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(({
  label,
  value: radioValue,
  disabled: localDisabled,
  className = '',
  ...props
}, ref) => {
  const group = useContext(RadioGroupContext);
  if (!group) {
    throw new Error('RadioButton must be used inside a RadioGroup');
  }

  const isSelected = group.value === radioValue;
  const isDisabled = localDisabled || group.disabled;

  const handleChange = useCallback(() => {
    if (!isDisabled) {
      group.onChange(radioValue);
    }
  }, [group, radioValue, isDisabled]);

  const wrapperClasses = [
    styles.wrapper,
    isSelected ? styles.selected : '',
    isDisabled ? styles.disabled : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <label className={wrapperClasses}>
      <input
        ref={ref}
        type="radio"
        className={styles.input}
        name={group.name}
        value={radioValue}
        checked={isSelected}
        disabled={isDisabled}
        onChange={handleChange}
        tabIndex={isSelected ? 0 : -1}
        {...props}
      />
      <span className={styles.circle}>
        <span className={styles.dot} />
      </span>
      {label && <span className={styles.label}>{label}</span>}
    </label>
  );
});

RadioButton.displayName = 'RadioButton';
export default RadioButton;
